import React, {
  createContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';

export interface INotificationContext {
  isVisible: boolean;
  setIsVisible: (value: boolean) => void;
  notificationType: NotificationType;
  setNotificationType: React.Dispatch<React.SetStateAction<NotificationType>>;
  title: string;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
}

export enum NotificationType {
  DEFAULT = 'default',
  ERROR = 'error',
  SUCCESS = 'success',
  WARNING = 'warning',
}

export const NotificationContext = createContext<INotificationContext>({
  isVisible: false,
  setIsVisible: () => undefined,
  notificationType: NotificationType.DEFAULT,
  setNotificationType: () => undefined,
  title: '',
  setTitle: () => undefined,
  message: '',
  setMessage: () => undefined,
});

interface NotificationProviderProps {
  children: React.ReactNode;
}

const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [timerId, setTimerId] = useState<ReturnType<typeof setTimeout>>();
  const [isVisible, setIsVisible] = useState(false);
  const [notificationType, setNotificationType] = useState<NotificationType>(
    NotificationType.DEFAULT
  );
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const handleSetIsVisible = useCallback((value: boolean) => {
    if (value === false) {
      setIsVisible(false);
      return;
    }

    if (timerId) {
      clearTimeout(timerId);
    }
    setIsVisible(true);

    const newTimerId = setTimeout(() => {
      setIsVisible(false);
    }, 5000);
    setTimerId(newTimerId);
  }, []);

  useEffect(() => () => clearTimeout(timerId), [timerId]);

  const value = useMemo(
    () => ({
      isVisible,
      setIsVisible: handleSetIsVisible,
      notificationType,
      setNotificationType,
      title,
      setTitle,
      message,
      setMessage,
    }),
    [
      isVisible,
      handleSetIsVisible,
      notificationType,
      setNotificationType,
      title,
      setTitle,
      message,
      setMessage,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
